import React, { useMemo } from 'react';
import {
  InstagramIcon,
  PinterestIcon,
  TwitterIcon,
  TiktokIcon,
  FacebookIcon,
  YoutubeIcon,
} from '@revfluence/fresh-icons/brands/esm';
import { CardUsernameDisplayProps } from '@/shadcn/components/card-overlay';
import type { SocialNetwork } from '@/applications/Social/types/social-network';

export interface UseCardUsernameDisplayOptions {
  /** Username to display */
  username?: string;
  /** Platform type for icon selection */
  platform?: SocialNetwork;
  /** Custom platform icon (overrides platform) */
  customIcon?: React.ReactNode;
  /** Additional CSS classes */
  className?: string;
  /** Whether to show tooltip on hover */
  showTooltip?: boolean;
  /** Custom tooltip content */
  tooltipContent?: React.ReactNode;
  /** Max width for username container */
  maxWidth?: string;
}

export interface UseCardUsernameDisplayResult {
  /** Props for CardUsernameDisplay component */
  cardUsernameDisplayProps: CardUsernameDisplayProps | null;
  /** Whether username should be displayed */
  shouldDisplay: boolean;
}

/**
 * useCardUsernameDisplay
 * ----------------------
 * Hook to build props for CardUsernameDisplay component.
 * Provides platform-specific icon selection and validation logic.
 *
 * @param options - Configuration options for username display
 * @returns Object with cardUsernameDisplayProps and shouldDisplay flag
 */
export const useCardUsernameDisplay = (options: UseCardUsernameDisplayOptions = {}): UseCardUsernameDisplayResult => {
  const {
    username,
    platform = 'instagram',
    customIcon,
    className,
    showTooltip = true,
    tooltipContent,
    maxWidth = 'max-w-[calc(100%-4.5rem)]',
  } = options;

  const platformIcon = useMemo(() => {
    if (customIcon) return customIcon;

    const iconProps = { className: 'h-4 w-4 text-white drop-shadow-lg flex-shrink-0' };
    const platformIconMap = {
      instagram: InstagramIcon,
      twitter: TwitterIcon,
      tiktok: TiktokIcon,
      facebook: FacebookIcon,
      youtube: YoutubeIcon,
      pinterest: PinterestIcon,
    } satisfies Record<SocialNetwork, React.ElementType>;

    const IconComponent = platformIconMap[platform];
    return IconComponent ? <IconComponent {...iconProps} /> : null;
  }, [platform, customIcon]);

  const shouldDisplay = Boolean(username);

  const cardUsernameDisplayProps = useMemo<CardUsernameDisplayProps | null>(() => {
    if (!shouldDisplay || !username) return null;

    return {
      username,
      platformIcon,
      className,
      showTooltip,
      tooltipContent,
      maxWidth,
    };
  }, [username, platformIcon, className, showTooltip, tooltipContent, maxWidth, shouldDisplay]);

  return {
    cardUsernameDisplayProps,
    shouldDisplay,
  };
};

/**
 * useInstagramUsernameDisplay
 * ---------------------------
 * Preset hook for Instagram username display.
 */
export const useInstagramUsernameDisplay = (username?: string, maxWidth?: string): UseCardUsernameDisplayResult => {
  return useCardUsernameDisplay({
    username,
    platform: 'instagram',
    maxWidth,
  });
};

/**
 * useTikTokUsernameDisplay
 * ------------------------
 * Preset hook for TikTok username display.
 */
export const useTikTokUsernameDisplay = (username?: string, maxWidth?: string): UseCardUsernameDisplayResult => {
  return useCardUsernameDisplay({
    username,
    platform: 'tiktok',
    maxWidth,
  });
};
